/** @jsxImportSource ./simboluri-jsx */
// Capitolul 5 — LISTA ELEMENTELOR CU VERDICT (mock-ui/design/StrLista.dc.html):
// înlocuiește select-ul de 60 de poziții fără verdict. Sortarea e fixă și
// spune povestea: întâi ce pică, apoi rezerva cea mai mică. Filtrele sunt
// chips, nu dropdown-uri.

import { useMemo, useState } from "react";
import { useT } from "../i18n";
import type { Cheie } from "../i18n";
import { StepState } from "./tipuri";
import {
  ElementIndexat,
  TIP_ETICHETA,
  VERDICT_CHIP,
  indexElemente,
  nr,
} from "./elemente";
import { hashElement, hashSectiune } from "./legaturi";

const ORDINE_VERDICT = { neconform: 0, la_limita: 1, conform: 2 } as const;

const TIPURI: { key: ElementIndexat["tip"] | "toate"; eticheta: Cheie }[] = [
  { key: "toate", eticheta: "struct.lista.tip_toate" },
  { key: "grinda", eticheta: "struct.lista.tip_grinzi" },
  { key: "stalp", eticheta: "struct.lista.tip_stalpi" },
  { key: "lamela", eticheta: "struct.lista.tip_lamele" },
  { key: "perete", eticheta: "struct.lista.tip_pereti" },
  { key: "ochi", eticheta: "struct.lista.tip_ochiuri" },
];

export default function ElementeLista({
  id,
  solutie,
  steps,
}: {
  id: string;
  solutie?: string | null;
  steps: Record<string, StepState>;
}) {
  const t = useT();
  const toate = useMemo(() => indexElemente(steps), [steps]);
  const [tip, setTip] = useState<ElementIndexat["tip"] | "toate">("toate");
  const [nivel, setNivel] = useState<string | null>(null);
  const [doarProbleme, setDoarProbleme] = useState(false);
  // câte rânduri conforme se văd implicit (restul, la cerere)
  const [arataToate, setArataToate] = useState(false);

  const niveluri = useMemo(
    () => [...new Set(toate.map((e) => e.nivel).filter(Boolean))],
    [toate]
  );

  const filtrate = useMemo(() => {
    let f = toate;
    if (tip !== "toate") f = f.filter((e) => e.tip === tip);
    if (nivel) f = f.filter((e) => e.nivel === nivel);
    if (doarProbleme) f = f.filter((e) => e.verdict !== "conform");
    return [...f].sort((a, b) => {
      const dv = ORDINE_VERDICT[a.verdict] - ORDINE_VERDICT[b.verdict];
      if (dv !== 0) return dv;
      return (a.reMin ?? Infinity) - (b.reMin ?? Infinity);
    });
  }, [toate, tip, nivel, doarProbleme]);

  const nrPe = (v: keyof typeof ORDINE_VERDICT) =>
    (tip === "toate" ? toate : toate.filter((e) => e.tip === tip)).filter(
      (e) => e.verdict === v
    ).length;

  if (toate.length === 0) {
    return (
      <div className="ob-pagina ob-pagina-ingusta">
        <div className="ob-pipe-head"><h2>{t("struct.lista.titlu_gol")}</h2></div>
        <div className="sg-muted">
          {t("struct.lista.gol")}{" "}
          <a href={hashSectiune(id, "pipeline", solutie ?? null)}>
            {t("struct.lista.pipeline")}
          </a>
        </div>
      </div>
    );
  }

  const probleme = filtrate.filter((e) => e.verdict !== "conform");
  const conforme = filtrate.filter((e) => e.verdict === "conform");
  const vizibile = arataToate || doarProbleme ? filtrate : [...probleme, ...conforme.slice(0, 25)];
  const ascunse = filtrate.length - vizibile.length;

  return (
    <div className="ob-pagina" style={{ maxWidth: 1100, margin: "0 auto" }}>
      <div className="ob-pipe-head">
        <h2>{t("struct.lista.titlu")}</h2>
        <span className="ob-acasa-sub">
          {t("struct.lista.sub")}
        </span>
      </div>

      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
        <span className="ob-lbl">{t("struct.lista.filtru_tip")}</span>
        {TIPURI.map((x) => (
          <button
            key={x.key}
            className={`ob-chip ${tip === x.key ? "ob-c-acc" : "ob-c-mut"}`}
            style={{ cursor: "pointer", border: "none", font: "inherit", fontSize: 11 }}
            onClick={() => setTip(x.key)}
          >
            {t(x.eticheta)}
          </button>
        ))}
        <span className="ob-lbl" style={{ marginLeft: 12 }}>{t("struct.lista.filtru_verdict")}</span>
        <span className="ob-chip ob-c-bad">{t("struct.lista.neconforme", { n: nrPe("neconform") })}</span>
        <span className="ob-chip ob-c-amb">{t("struct.lista.la_limita", { n: nrPe("la_limita") })}</span>
        <button
          className={`ob-chip ${doarProbleme ? "ob-c-acc" : "ob-c-mut"}`}
          style={{ cursor: "pointer", border: "none", font: "inherit", fontSize: 11 }}
          onClick={() => setDoarProbleme(!doarProbleme)}
          title={t("struct.lista.doar_probleme_titlu")}
        >
          {t("struct.lista.doar_probleme")}
        </button>
        <span className="ob-lbl" style={{ marginLeft: 12 }}>{t("struct.lista.filtru_nivel")}</span>
        {niveluri.map((n) => (
          <button
            key={n}
            className={`ob-chip ${nivel === n ? "ob-c-acc" : "ob-c-mut"}`}
            style={{ cursor: "pointer", border: "none", font: "inherit", fontSize: 11 }}
            onClick={() => setNivel(nivel === n ? null : n)}
          >
            {n}
          </button>
        ))}
      </div>

      <div className="ob-card" style={{ overflow: "hidden" }}>
        <table className="ob-sol-tabel">
          <thead>
            <tr>
              <th style={{ width: 90 }}>{t("verificari.col_element")}</th>
              <th style={{ width: 70 }}>{t("struct.lista.col_tip")}</th>
              <th style={{ width: 90 }}>{t("struct.top.nivel")}</th>
              <th style={{ width: 110 }}>{t("struct.lista.col_sectiune")}</th>
              <th style={{ width: 80, textAlign: "right" }}>{t("struct.lista.col_re")}</th>
              <th style={{ width: 110 }}>{t("struct.lista.col_verdict")}</th>
              <th>{t("struct.lista.col_criteriu")}</th>
              <th style={{ width: 90 }} />
            </tr>
          </thead>
          <tbody>
            {vizibile.map((e) => {
              const chip = VERDICT_CHIP[e.verdict];
              const culoare =
                e.verdict === "neconform"
                  ? "var(--bad)"
                  : e.verdict === "la_limita"
                    ? "var(--amb)"
                    : "var(--mut)";
              return (
                <tr key={`${e.tip}:${e.ref}:${e.nivel}`}>
                  <td><span className="ob-ref">{e.ref}</span></td>
                  <td style={{ fontSize: 11, color: "var(--mut)" }}>{t(TIP_ETICHETA[e.tip])}</td>
                  <td style={{ fontSize: 12, color: "var(--mut)" }}>{e.nivel}</td>
                  <td className="sg-mono" style={{ fontSize: 12 }}>{e.sectiune}</td>
                  <td className="sg-mono" style={{ textAlign: "right", color: culoare, fontWeight: e.verdict === "neconform" ? 600 : 400 }}>
                    {e.reMin != null ? nr(e.reMin, 2) : "—"}
                  </td>
                  <td><span className={`ob-chip ${chip.cls}`} style={{ fontSize: 10 }}>{t(chip.text)}</span></td>
                  <td style={{ fontSize: 12, color: "var(--mut)" }}>{e.criteriu || "—"}</td>
                  <td style={{ textAlign: "right" }}>
                    <a href={hashElement(id, e.ref, solutie)} style={{ fontSize: 12 }}>
                      {t("struct.lista.deschide")}
                    </a>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {ascunse > 0 && (
          <div style={{ padding: "10px 14px", borderTop: "1px solid var(--line)", fontSize: 12, color: "var(--mut)" }}>
            {t("struct.lista.ascunse", { n: nr(ascunse) })}
            <a href="#" onClick={(ev) => { ev.preventDefault(); setArataToate(true); }}>
              {t("struct.lista.arata_toate")}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
